import React, { useState } from 'react';
import { Step } from 'react-joyride';
import { useHistory } from 'react-router-dom';

import AppBar from 'components/AppBar';
import FloatingButton from 'components/FloatingButton';
import GroupedList from 'components/GroupedList';
import GroupedItem from 'components/GroupedList/components/GroupedItem';
import Item from 'components/List/StyledListItem';
import Loader from 'components/Loader';
import { Desktop, Mobile } from 'components/Responsive';
import ScreenHeader from 'components/ScreenHeader';
import Search from 'components/Search';
import Tour from 'components/Tour';
import useStoreTranslation from 'hooks/useStoreTranslation';
import { htmlT } from 'utils/generic';
import { TravelReason } from 'models/travelReseller';
import TravelReasonEmptyList from './components/TravelReasonEmptyList';
import { List, Container } from './styles';
import { useSelector } from 'react-redux';
import { RootState } from 'store';
import ConfirmBottomSheet from 'components/ConfirmBottomSheet';
import { deleteTravelReason } from 'services/resellerTravel';
import useToast from 'hooks/useToast';
import { CloseButton, CloseIcon } from 'components/ModalHeader/styles';

interface Props {
  travelTarget?: string;
  travelReasonList: TravelReason[];
  setTravelReasonList: React.Dispatch<React.SetStateAction<TravelReason[]>>;
  isLoading: boolean;
  tourHasAccess?: boolean;
  tourLoading: boolean;
  ruleSelected?: TravelReason;
  onCloseSelectionScreen?: () => void;
  onConfirmSelection?: (rule: TravelReason) => void;
  onDeleteSuccess?: () => void;
}

const TravelReasonList = ({ travelReasonList, setTravelReasonList, isLoading, tourLoading, onDeleteSuccess }: Props) => {
  const [showSearch, setShowSearch] = useState(false);
  const [travelResult, setRulesResult] = useState<TravelReason[]>([]);
  const [travelReasonSelected, setTravelReasonSelected] = useState<TravelReason | undefined>(undefined);
  const [showConfirmDelete, setShowConfirmDelete] = useState(false);
  const toast = useToast();
  const history = useHistory();
  const { t } = useStoreTranslation();
  const travelData = { isAddButton: true };
  /* estados globales */
  const accountResult = useSelector((state: RootState) => state.user.accountResult);

  const handleSelection = (travelReasonId: TravelReason) => {
    history.replace(`/corporate/resellerForm/${travelReasonId.id}`);
  };

  const handleOnSearch = (value: string) => {
    if (value) {
      setShowSearch(true);
      setRulesResult(
        travelReasonList.filter(
          (name: TravelReason) => name.name && name.name.toLowerCase().includes(value.toLowerCase()),
        ),
      );
    } else {
      setShowSearch(false);
    }
  };
  const handleDelete = async () => {
    try {
      const data = await deleteTravelReason(travelReasonSelected?.id);
      if (data === 'ok') {
        toast.add({
          type: 'success',
          message: t('resellerForm.delete.success'),
        });
        onDeleteSuccess?.();
      }
    } catch (error) {}
  };

  const handleConfirmDelete = (travel?: TravelReason) => {
    if (travelReasonList.length === 1) {
      toast.add({ type: 'danger', message: t('resellerForm.deleteLastReason.error') });
      return;
    }
    setShowConfirmDelete(true);
    setTravelReasonSelected(travel);
  };

  const renderList = () => (
    <>
      <Search show={travelReasonList.length >= 10} onSearch={handleOnSearch} />

      <Mobile>
        {showSearch ? (
          <List>
            <GroupedItem
              quantityText={htmlT(t, 'groupList.searchResults', { count: travelResult?.length })}
              isSearch={showSearch}
              items={travelResult}
              selectableType="arrow"
              onSelection={handleSelection}
            />
          </List>
        ) : (
          <>
            <GroupedList
              isAlphabetic
              isSelectable={false}
              items={travelReasonList}
              isSearch={showSearch}
              selectableType="arrow"
              onSelection={handleSelection}
            />
          </>
        )}
      </Mobile>

      <Desktop>
        <List>
          {(showSearch ? travelResult : travelReasonList).map((travel) => (
            <Item
              key={`user-${travel.id}`}
              title={travel.name || ''}
              deleteMode={!travel.isFromReseller}
              forceEdit={!travel.isFromReseller}
              disabled={travel.isFromReseller}
              onDelete={() => handleConfirmDelete(travel)}
              onClick={() => handleSelection(travel)}
            />
          ))}
        </List>
        <ConfirmBottomSheet
          show={showConfirmDelete}
          title={t('resellerForm.confirmDelete.title')}
          onClose={() => setShowConfirmDelete(false)}
          content={t('resellerForm.confirmDelete.text')}
          confirmText={t('rule.delete.bottomSheetButton')}
          onConfirm={() => handleDelete()}
        />
      </Desktop>
    </>
  );
  const initialSteps: Step[] = [
    {
      target: '.target',
      content: t('groupList.addButtonTour'),
      disableBeacon: true,
      placement: 'center',
    },
  ];

  return (
    <>
      <Mobile>
        <AppBar goBack={() => history.replace('/corporate/configurations')}>
          <h5>{t('travelReason.title')}</h5>
        </AppBar>
      </Mobile>

      {isLoading ? (
        <Loader fullScreen />
      ) : (
        <>
          <Desktop>
            <ScreenHeader
              title={t('travelReason.title')}
              subtitle={t('travelReason.description')}
              nameCompany={t('travelReason.nameCompany', { company: accountResult.companyName })}
              right={
                <CloseButton onClick={() => history.goBack()}>
                  <CloseIcon />
                </CloseButton>
              }
            />
            
          </Desktop>

          <Container hasList={travelReasonList.length > 0}>
            <Tour
              type="cancelable"
              show={!tourLoading /* && !tourHasAccess */}
              hideArrow
              disableOverlayClose
              disableScrollParentFix
            />
            {travelReasonList && travelReasonList.length ? renderList() : <TravelReasonEmptyList />}
          </Container>
        </>
      )}
      <FloatingButton
        zIndex={2}
        onClick={() => history.replace({ pathname: '/corporate/resellerForm', state: { travelData } })}
        className="target"
      />
    </>
  );
};

export default TravelReasonList;
